import type { StoryEntity, TagEntity } from '@schibsted/frapi-schema';
import type {
  BaseEventOrigin,
  IViewArticle,
  IViewError,
  IViewFrontpage,
  IViewListing,
  IViewLockedArticle,
  IViewPage,
  ViewArticleInputProperties,
  ViewListingInputProperties,
  ViewPageInputProperties,
  ViewSalesPosterInputObject,
  ViewSalesPosterInputProperties,
} from '@schibsted/pulse-sdk';
import type { AuthorResponse } from '../../../../../core/features/author-bio/types.js';
import type { CuratePulseTrackingCustomProperties } from '../../../../../core/utils/types.js';
import type {
  ByfunnFrapiArticle,
  ByfunnPromoArticle,
} from '../../../../types/frapi-types.js';
import type { ByfunnPulseContextInputParams } from '../../../types.js';
import type { SortConfig } from '../../../../views/layouts/front/sectionHelpers.js';

import { getBlendMetrics } from '../../../../../core/mandatory-integrations/pulse/utils/metrics.js';
import {
  accessLevel,
  accessLevelName,
} from '../../../../../core/utils/access.js';
import { makeTagTitle } from '../../../../views/components/articles/helpers.js';
import { getAuthorEmails } from '../../../../views/components/articles/utils/authors.js';

const pathToHierarchy = (path: string): string =>
  path.replace(/^\/|\/$/g, '').replace(/\//g, '>');

const sorterToSortingMethod = (
  sorter?: SortConfig | null,
): IViewListing['object']['sortingMethod'] => {
  switch (sorter) {
    case 'date':
      return 'Time';
    case 'grade':
      return 'Other';
    case 'relevance':
    default:
      return 'Relevance';
  }
};

const buildArticlePageView = (
  article: ByfunnFrapiArticle,
): {
  legacy: ViewArticleInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewArticle>;
} => {
  const imageUrl = article.mainImage
    ? `https://premium.vgc.no/ap/images/${article.mainImage.id}?fit=crop&h=878&q=80&tight=true&w=2048`
    : undefined;

  const legacy: ViewArticleInputProperties = {
    object: {
      articleId: article.id,
      accessLevel: accessLevel(article.access),
      accessLevelName: accessLevelName(article.access),
      authors: getAuthorEmails(article),
      category: article.section,
      name: article.title,
      tags: article.tags.map((t) => t.title),
      wordCount: article.wordCount,
      'spt:custom': {
        'spt:wordCount': article.wordCount,
        'spt:authors': getAuthorEmails(article),
        'spt:published': article.published,
        'spt:update': article.updated,
        'spt:imageUrl': imageUrl,
      },
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewArticle> = {
    object: {
      category: article.section.toLowerCase(),
      authors: getAuthorEmails(article),
      isStacked: false,
      lastUpdatedDate: article.updated ?? article.published ?? '',
      newsroom: 'vink',
      objectId: article.id,
      publicationDate: article.published ?? '',
      title: article.title,
      topImageUrl: imageUrl,
      variant: 'Article',
      versionCounter: 0,
      wordCount: article.wordCount,
    },
    page: {
      content: {
        accessLevel: accessLevel(article.access),
        contentId: article.id,
        contentTitle: article.title,
        format: 'Article',
        formatSubtype: 'Article',
        namespace: 'vink',
      },
    },
  };

  return { legacy, tpaas };
};

const buildFrontpageView = (
  personalization: CuratePulseTrackingCustomProperties,
): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewFrontpage>;
} => {
  const metrics = getBlendMetrics(personalization.variant, personalization);

  const legacy: ViewListingInputProperties = {
    object: {
      id: 'frontpage',
      name: 'Frontpage - Vink',
    },
    metrics,
  };

  const tpaas: ByfunnPulseContextInputParams<IViewFrontpage> = {
    object: {
      objectId: 'frontpage-vink',
      title: 'Frontpage - Vink',
    },
    metrics,
  };

  return { legacy, tpaas };
};

const buildSectionPageView = ({
  sectionName,
  title,
  itemsTotal,
  personalization,
}: {
  sectionName: string;
  title: string;
  itemsTotal: number;
  personalization?: CuratePulseTrackingCustomProperties;
}): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewListing>;
} => {
  const metrics = personalization
    ? getBlendMetrics(personalization.variant, personalization)
    : undefined;

  const legacy: ViewListingInputProperties = {
    object: {
      id: 'section-page',
      name: sectionName,
    },
    metrics,
  };

  const tpaas: ByfunnPulseContextInputParams<IViewListing> = {
    object: {
      category: sectionName,
      contentSource: 'Editorial',
      isInfinite: false,
      itemsTotal,
      listingType: 'Section',
      objectId: `section:${sectionName}`,
      pageNumber: 0,
      sortingMethod: 'Editorial',
      title,
    },
    metrics,
  };

  return { legacy, tpaas };
};

const buildStaticPageView = ({
  pageType,
  componentName,
  title,
  currentUrl,
  sectionName,
}: {
  pageType: 'Listing' | 'Page';
  componentName: string;
  title: string;
  currentUrl: string;
  sectionName?: string;
}):
  | {
      pageType: 'Listing';
      legacy: ViewListingInputProperties;
      tpaas: ByfunnPulseContextInputParams<IViewListing>;
    }
  | {
      pageType: 'Page';
      legacy: ViewPageInputProperties;
      tpaas: ByfunnPulseContextInputParams<IViewPage>;
    } => {
  const pageId = componentName === 'error' ? 'error:404' : componentName;

  if (pageType === 'Listing') {
    return {
      pageType,
      legacy: {
        object: {
          id: 'section-page',
          name: sectionName ?? 'section-page',
        },
      },
      tpaas: {
        object: {
          category: pathToHierarchy(currentUrl),
          contentSource: 'Capi',
          isInfinite: false,
          itemsTotal: -1,
          listingType: 'Other',
          objectId: sectionName ?? pageId,
          pageNumber: 0,
          sortingMethod: 'Time',
          title,
        },
      },
    };
  }

  return {
    pageType,
    legacy: {
      object: {
        pageId,
        name: `Page - ${componentName}`,
      },
    },
    tpaas: {
      object: {
        category: sectionName ?? '',
        objectId: pageId,
        title,
      },
    },
  };
};

const buildErrorPageView = ({
  errorCode,
  errorMessage,
  title,
}: {
  errorCode: number;
  errorMessage: string;
  title: string;
}): {
  legacy: ViewPageInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewError>;
} => {
  const legacy: ViewPageInputProperties = {
    object: {
      pageId: `error:${errorCode}`,
      name: `Page - ${title}`,
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewError> = {
    object: {
      errorCode,
      errorMessage,
      objectId: `vink-${errorCode}`,
      title,
    },
  };

  return { legacy, tpaas };
};

const buildSearchPageView = ({
  currentUrl,
  itemsTotal,
}: {
  currentUrl: string;
  itemsTotal: number;
}): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewListing>;
} => {
  const phraseMatch = currentUrl.match(/phrase=([^&]*)/);
  const query = phraseMatch ? decodeURIComponent(phraseMatch[1]) : currentUrl;
  const filters = [...currentUrl.matchAll(/tagIds=([^&]*)/g)].flatMap((match) =>
    decodeURIComponent(match[1]).split('|'),
  );

  const legacy: ViewListingInputProperties = {
    object: {
      id: 'search',
      name: 'Search Result Page',
      'spt:custom': {
        query,
      },
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewListing> = {
    object: {
      category: 'sok',
      contentSource: 'Capi',
      isInfinite: false,
      filters,
      itemsTotal,
      listingType: 'Search',
      objectId: 'search',
      pageNumber: 0,
      searchQuery: query,
      sortingMethod: 'Relevance',
      title: 'Søk',
    },
  };

  return { legacy, tpaas };
};

const buildTagPageView = ({
  tag,
  currentUrl,
  pageNumber,
  itemsTotal,
}: {
  tag: TagEntity;
  currentUrl: string;
  pageNumber: number;
  itemsTotal: number;
}): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewListing>;
} => {
  const legacy: ViewListingInputProperties = {
    object: {
      id: 'tag',
      name: `tag:${tag.slug}`,
      'spt:custom': {
        currentUrl,
      },
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewListing> = {
    object: {
      category: pathToHierarchy(currentUrl),
      contentSource: 'Capi',
      isInfinite: false,
      itemsTotal,
      listingType: 'Tag',
      objectId: tag.id,
      pageNumber,
      sortingMethod: 'Time',
      title: tag.title,
    },
  };

  return { legacy, tpaas };
};

const buildTagCollectionPageView = ({
  sectionName,
  tags,
  itemsTotal,
  sorter,
}: {
  sectionName: string;
  tags: TagEntity[];
  itemsTotal: number;
  sorter?: SortConfig | null;
}): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewListing>;
} => {
  const tagTitles = tags.map((tag) => makeTagTitle(tag.title));

  const legacy: ViewListingInputProperties = {
    object: {
      id: `tag:${sectionName}`,
      name: `tag:${sectionName}`,
      'spt:custom': {
        tags: tagTitles,
      },
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewListing> = {
    object: {
      category: `samling>${sectionName}`,
      contentSource: 'Capi',
      filters: tagTitles.filter((title): title is string => Boolean(title)),
      isInfinite: false,
      itemsTotal,
      listingType: 'Tag',
      objectId: `tags:${sectionName}`,
      pageNumber: 0,
      sortingMethod: sorterToSortingMethod(sorter),
      title: `Tags: ${sectionName}`,
    },
  };

  return { legacy, tpaas };
};

const buildAuthorBioPageView = ({
  author,
  currentUrl,
  pageNumber,
  itemsTotal,
}: {
  author: Pick<AuthorResponse, 'id' | 'slug' | 'title'>;
  currentUrl: string;
  pageNumber: number;
  itemsTotal: number;
}): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewListing>;
} => {
  const legacy: ViewListingInputProperties = {
    object: {
      id: 'tag',
      name: author.slug,
      'spt:custom': {
        currentUrl,
      },
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewListing> = {
    object: {
      category: pathToHierarchy(currentUrl),
      contentSource: 'Capi',
      isInfinite: false,
      itemsTotal,
      listingType: 'Author',
      objectId: author.id,
      pageNumber,
      sortingMethod: 'Time',
      title: author.title,
    },
  };

  return { legacy, tpaas };
};

const buildStoryPageView = ({
  story,
  currentUrl,
  itemsTotal,
}: {
  story?: StoryEntity;
  currentUrl: string;
  itemsTotal: number;
}): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewListing>;
} => {
  const legacy: ViewListingInputProperties = {
    object: {
      id: 'story',
      name: 'story page',
      'spt:custom': {
        currentUrl,
      },
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewListing> = {
    object: {
      category: pathToHierarchy(currentUrl),
      contentSource: 'Capi',
      isInfinite: false,
      itemsTotal,
      listingType: 'Story',
      objectId: story?.id ?? 'story',
      pageNumber: 0,
      sortingMethod: 'Time',
      title: story?.title ?? 'Story page',
    },
  };

  return { legacy, tpaas };
};

const buildListPageView = ({
  currentUrl,
  title,
  itemsTotal,
  topListVersion,
}: {
  currentUrl: string;
  title: string;
  itemsTotal: number;
  /** Omit for master lists. */
  topListVersion?: 1 | 2;
}): {
  legacy: ViewListingInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewListing>;
} => {
  const isTopList = topListVersion !== undefined;
  const objectId = isTopList ? 'list-page:top-list' : 'list-page:master-list';

  const legacy: ViewListingInputProperties = {
    object: {
      id: objectId,
      name: `list page - ${isTopList ? 'top list' : 'master list'}`,
      'spt:custom': {
        currentUrl,
        ...(isTopList ? { topListVersion } : {}),
      },
    },
  };

  const tpaas: ByfunnPulseContextInputParams<IViewListing> = {
    object: {
      category: pathToHierarchy(currentUrl),
      contentSource: 'Editorial',
      isInfinite: false,
      itemsTotal,
      listingType: 'Other',
      objectId: isTopList ? `${objectId}:v${topListVersion}` : objectId,
      pageNumber: 0,
      sortingMethod: 'Editorial',
      title,
    },
  };

  return { legacy, tpaas };
};

const buildSalesposterView = ({
  article,
  origin,
}: {
  article: ByfunnPromoArticle;
  origin: BaseEventOrigin;
}): {
  legacy: ViewSalesPosterInputProperties;
  tpaas: ByfunnPulseContextInputParams<IViewLockedArticle>;
} => {
  const legacy: {
    object: ViewSalesPosterInputObject;
    origin: BaseEventOrigin;
  } = {
    object: {
      articleId: article.id,
      accessLevel: 'Paid',
      accessLevelName: 'Paid',
      authors: getAuthorEmails(article),
      category: article.section,
      name: article.title,
      tags: article.tags.map((t) => t.title),
      wordCount: article.wordCount,
      salesPosterType: 'Paid',
      'spt:custom': {
        'spt:published': article.published,
        'spt:update': article.updated,
      },
    },
    origin,
  };

  const tpaas: ByfunnPulseContextInputParams<IViewLockedArticle> = {
    object: {
      category: article.section.toLowerCase(),
      authors: getAuthorEmails(article),
      isStacked: false,
      lastUpdatedDate: article.updated ?? article.published ?? '',
      newsroom: 'vink',
      objectId: article.id,
      offerFoldPosition: 'Unknown',
      paywallType: 'Paid',
      publicationDate: article.published ?? '',
      title: article.title,
      variant: 'Article',
      versionCounter: 0,
      wordCount: article.wordCount,
    },
    page: {
      content: {
        accessLevel: 'Paid',
        contentId: article.id,
        contentTitle: article.title,
        format: 'Article',
        formatSubtype: 'Article',
        namespace: 'vink',
      },
    },
  };

  return { legacy, tpaas };
};

export {
  buildArticlePageView,
  buildAuthorBioPageView,
  buildErrorPageView,
  buildFrontpageView,
  buildListPageView,
  buildSalesposterView,
  buildSearchPageView,
  buildSectionPageView,
  buildStaticPageView,
  buildStoryPageView,
  buildTagCollectionPageView,
  buildTagPageView,
};
